import { useState, useEffect } from 'react';
import {
  TextField, Button, Box,
  Typography, InputAdornment,
  Link, Alert
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import SpaIcon from '@mui/icons-material/Spa';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { loginSuccess } from '../redux/authSlice';
import logoNoText from '../images/Vector.svg'
import backgroundLogin from '../images/sfondo_login_page.png'
import { useTheme } from '@mui/material';
import { useSnackbar } from '../contexts/SnackBarContext';
import { requestPasswordReset, resetPasswordWithToken } from '../APIs/AuthAPI';
import { localizedErrorMessage } from '../utils/italianErrorMessage';

/*
credenziali admin
{
  "email": "Admin123",
  "password": "Password123!"
}
*/

const passwordRules = [
  { regex: /.{8,}/, messageKey: 'error_password_length' },
  { regex: /[A-Z]/, messageKey: 'error_password_upper' },
  { regex: /[a-z]/, messageKey: 'error_password_lower' },
  { regex: /[0-9]/, messageKey: 'error_password_number' },
  { regex: /[!@#$%^&*(),.?":{}|<>]/, messageKey: 'error_password_special' },
];

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const usernameRegex = /^[a-zA-Z0-9._-]{3,}$/;

const tenantTypeOptions = {
  RISTORANTE: { label: 'Ristorante', icon: RestaurantIcon },
  SPA: { label: 'SPA', icon: SpaIcon },
  SALE_CONGRESSI: { label: 'Sale congressi', icon: MeetingRoomIcon },
};

function getPasswordErrorKeys(password) {
  return passwordRules
    .filter(rule => !rule.regex.test(password))
    .map(rule => rule.messageKey);
}
// Normalizza il tenant type, restituendo un valore standardizzato o un default
function normalizedTenantType(type) {
  if (!type) return null;

  const normalized = String(type).trim().toLowerCase();

  if (normalized === 'ristorante') return 'ristorante';
  if (normalized === 'spa') return 'spa';
  if (normalized === 'sale_congressi') return 'sale_congressi';


  return null;
}

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();

  const [showPassword, setShowPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [username, setUsername] = useState();
  const [password, setPassword] = useState();
  const [errorKeys, setErrorKeys] = useState([]);
  const [isUsernameValid, setIsUsernameValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [availableTenantTypes, setAvailableTenantTypes] = useState([]);
  const [selectedTenantType, setSelectedTenantType] = useState(null);

  const isResetPasswordRoute = location.pathname === '/reset-password';


  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = () => setShowPassword(!showPassword);

  useEffect(() => {
    const valid = emailRegex.test(username) || usernameRegex.test(username);
    setIsUsernameValid(valid);
  }, [username]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setResetToken(params.get('token') || '');
  }, [location.search]);

  const handleLogin = async (tenantType = null) => {
    const keys = [];

    if (!username) keys.push('error_username_required');
    if (!password) keys.push('error_password_required');

    if (keys.length > 0) {
      setErrorKeys(keys);
      return;
    }

    setErrorKeys([]);
    setLoading(true);
    setSelectedTenantType(tenantType);

    try {
      // 1) tentativo login "classico" tenant
      let response = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: username,
          password,
          ...(tenantType ? { tenantType } : {}),
        })
      });

      const tenantData = await response.json().catch(() => ({}));

      if (response.ok) {
        const data = tenantData;
        const accessToken = data.accessToken || data.token;
        const refreshToken = data.refreshToken;
        const tenantId = data.tenantId;
        const tenantType = normalizedTenantType(data.tenantType || null);
        const user = data.user;


        if (!tenantType) {
          handleAlert(t("admin.tenants.miss") + data.tenantType, "warning");
        }

        dispatch(loginSuccess({ accessToken, refreshToken, tenantId, tenantType: tenantType || null }));

        if (user?.id) {
          localStorage.setItem('id', user.id);
        }

        setErrorKeys([]);
        const normalizedTenantRoute = String(tenantType || '').toUpperCase();
        const nextRoute = normalizedTenantRoute === 'SPA'
          ? '/spa'
          : normalizedTenantRoute === 'SALE_CONGRESSI'
            ? '/prenotazione'
            : '/landing_page';

        navigate(nextRoute, { replace: true });
        return;
      }

      if (response.status === 409 && tenantData.code === 'TENANT_TYPE_REQUIRED') {
        const availableTypes = Array.isArray(tenantData.availableTenantTypes)
          ? tenantData.availableTenantTypes.filter((type) => tenantTypeOptions[type])
          : [];
        setAvailableTenantTypes(availableTypes);
        setErrorKeys([]);
        return;
      }

      if (tenantType) {
        throw new Error(tenantData.error || 'Login failed');
      }

      // 2) fallback: se non è un utente tenant, prova login superadmin
      response = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: username, password })
      });

      if (!response.ok) {
        // entrambi i login falliti => errore standard
        throw new Error((await response.json().catch(() => ({})))?.error || 'Login failed');
      }

      // Login superadmin riuscito => redirect a admin_page
      const adminData = await response.json();
      const accessToken = adminData.accessToken || adminData.token;
      const refreshToken = adminData.refreshToken;
      const user = adminData.user;

      // per superadmin tipicamente non c’è tenantId
      dispatch(loginSuccess({ accessToken, refreshToken, tenantId: null }));

      if (user?.id) {
        localStorage.setItem('id', user.id);
      }

      localStorage.setItem('isSuperadmin', '1');// opzionale, utile per guard lato FE

      setErrorKeys([]);
      navigate('/admin_page', { replace: true });

    } catch (error) {

      setErrorKeys(['error_password_incorrect']);
    } finally {
      setLoading(false);
      setSelectedTenantType(null);
    }
  };

  const resetTenantSelection = () => {
    setAvailableTenantTypes([]);
    setSelectedTenantType(null);
    setErrorKeys([]);
  };

  function handleAlert(msg, type) {
    showSnackbar(msg, type)
  }

  const handleRequestPasswordReset = async () => {
    const email = resetEmail.trim();
    if (!emailRegex.test(email)) {
      setResetError('Inserisci un indirizzo email valido.');
      return;
    }

    setResetError('');
    setResetLoading(true);
    try {
      const result = await requestPasswordReset(email);
      setResetEmailSent(true);
      if (result?.devResetToken) {
        setResetToken(result.devResetToken);
      }
      handleAlert('Se l\'account esiste, riceverai le istruzioni via email.', 'success');
    } catch (error) {
      setResetError(localizedErrorMessage(error, 'Errore durante la richiesta di reset password.'));
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async () => {
    const token = resetToken.trim();
    const validationErrors = getPasswordErrorKeys(newPassword);

    if (!token) {
      setResetError('Token reset password mancante.');
      return;
    }

    if (validationErrors.length > 0) {
      setResetError(validationErrors.map(key => t(key)).join(' '));
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setResetError('Le password non coincidono.');
      return;
    }

    setResetError('');
    setResetLoading(true);
    try {
      await resetPasswordWithToken({ token, newPassword });
      handleAlert('Password aggiornata correttamente. Ora puoi accedere.', 'success');
      setNewPassword('');
      setConfirmNewPassword('');
      setResetToken('');
      navigate('/login', { replace: true });
    } catch (error) {
      setResetError(localizedErrorMessage(error, 'Token non valido o scaduto.'));
    } finally {
      setResetLoading(false);
    }
  };

  const renderResetPasswordForm = () => (
    <>
      <Typography variant="h5" fontWeight={800} textAlign="center" sx={{ mb: 1 }}>
        Reset password
      </Typography>
      <Typography variant="body2" textAlign="center" sx={{ mb: 3, opacity: 0.8 }}>
        Inserisci la nuova password per completare il reset.
      </Typography>

      {resetError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {resetError}
        </Alert>
      )}

      <TextField
        label="Token"
        fullWidth
        value={resetToken}
        onChange={(e) => setResetToken(e.target.value)}
        margin="normal"
        helperText="Viene compilato automaticamente dal link email. Se hai ricevuto solo il token, incollalo qui."
      />

      <TextField
        label="Nuova password"
        type={showResetPassword ? "text" : "password"}
        fullWidth
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        margin="normal"
        InputProps={{
          endAdornment: (
            <InputAdornment position='end'>
              <Button color='primary' onClick={() => setShowResetPassword(prev => !prev)}>
                {showResetPassword ? <VisibilityOffIcon fontSize='small' /> : <VisibilityIcon fontSize='small' />}
              </Button>
            </InputAdornment>
          )
        }}
      />

      <TextField
        label="Conferma password"
        type={showResetPassword ? "text" : "password"}
        fullWidth
        value={confirmNewPassword}
        onChange={(e) => setConfirmNewPassword(e.target.value)}
        margin="normal"
      />

      <Button
        variant='contained'
        color='primary'
        fullWidth
        onClick={handleResetPassword}
        disabled={resetLoading}
        sx={{ height: '48px', marginTop: '16px' }}
      >
        {resetLoading ? t('loading') : 'Aggiorna password'}
      </Button>

      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Link component="button" type="button" onClick={() => navigate('/login')}>
          Torna al login
        </Link>
      </Box>
    </>
  );

  const renderForgotPasswordForm = () => (
    <>
      <Typography variant="h5" fontWeight={800} textAlign="center" sx={{ mb: 1 }}>
        Recupera password
      </Typography>
      <Typography variant="body2" textAlign="center" sx={{ mb: 3, opacity: 0.8 }}>
        Inserisci l'email dell'operatore. Se l'account esiste, riceverai un link di reset.
      </Typography>

      {resetError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {resetError}
        </Alert>
      )}

      {resetEmailSent && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Richiesta inviata. Controlla l'email e apri il link ricevuto.
        </Alert>
      )}

      <TextField
        label={t('email')}
        type="email"
        fullWidth
        value={resetEmail}
        onChange={(e) => setResetEmail(e.target.value)}
        margin="normal"
      />

      <Button
        variant='contained'
        color='primary'
        fullWidth
        onClick={handleRequestPasswordReset}
        disabled={resetLoading}
        sx={{ height: '48px', marginTop: '16px' }}
      >
        {resetLoading ? t('loading') : 'Invia link reset'}
      </Button>

      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Link
          component="button"
          type="button"
          onClick={() => {
            setShowForgotPassword(false);
            setResetError('');
          }}
        >
          Torna al login
        </Link>
      </Box>
    </>
  );

  const renderTenantSelection = () => (
    <Box sx={{ width: '100%' }}>
      <Typography
        component="h1"
        sx={{
          fontSize: { xs: '1.55rem', sm: '2rem' },
          fontWeight: 800,
          textAlign: 'center',
          lineHeight: 1.2,
        }}
      >
        A quale tenant vuoi accedere?
      </Typography>
      <Typography
        variant="body2"
        textAlign="center"
        sx={{ mt: 1, mb: { xs: 2.5, sm: 3.5 }, opacity: 0.72, wordBreak: 'break-word' }}
      >
        {username}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
          gap: { xs: 1.5, sm: 2 },
        }}
      >
        {availableTenantTypes.map((type) => {
          const option = tenantTypeOptions[type];
          const Icon = option.icon;
          const isCurrentSelection = loading && selectedTenantType === type;

          return (
            <Button
              key={type}
              variant="contained"
              onClick={() => handleLogin(type)}
              disabled={loading}
              startIcon={<Icon sx={{ fontSize: '2rem !important' }} />}
              sx={{
                minHeight: { xs: 76, sm: 142 },
                px: 2,
                borderRadius: 3,
                flexDirection: { xs: 'row', sm: 'column' },
                gap: { xs: 1, sm: 1.5 },
                fontSize: { xs: '1rem', sm: '1.05rem' },
                fontWeight: 800,
                textTransform: 'none',
                boxShadow: '0 10px 24px rgba(212,114,0,0.24)',
              }}
            >
              {isCurrentSelection ? t('loading') : option.label}
            </Button>
          );
        })}
      </Box>

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Link component="button" type="button" onClick={resetTenantSelection} disabled={loading}>
          Accedi con un altro account
        </Link>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundImage: `url(${backgroundLogin})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        overflowY: 'auto',
        py: { xs: 2, sm: 3 },
      }}
    >
      {/* overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            theme.palette.mode === 'dark'
              ? 'rgba(0,0,0,0.55)'
              : 'rgba(0,0,0,0.35)',
        }}
      />

      {/* centered content */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          px: { xs: 2, sm: 3 },
        }}
      >
        <Box sx={{
          width: availableTenantTypes.length > 0 ? '760px' : '440px',
          maxWidth: '100%',
          minHeight: availableTenantTypes.length > 0 ? 'auto' : { xs: 'auto', sm: '580px' },
          backgroundColor:
            theme.palette.mode === 'dark'
              ? 'rgba(24,24,24,0.92)'
              : 'rgba(255,255,255,0.95)',
          color: theme.palette.text.primary,
          borderRadius: '14px',
          boxShadow:
            theme.palette.mode === 'dark'
              ? '0 25px 80px rgba(0,0,0,0.65)'
              : '0 25px 80px rgba(0,0,0,0.4)',
          border:
            theme.palette.mode === 'dark'
              ? '1px solid rgba(255,255,255,0.08)'
              : '1px solid rgba(0,0,0,0.06)',
          padding: { xs: '24px', sm: '32px' },
          transition: 'width 220ms ease',
        }}>
          <Box sx={{ pb: availableTenantTypes.length > 0 ? { xs: 2, sm: 3 } : '50px' }}>
            <img
              src={logoNoText}
              alt=""
              style={{
                display: "block",
                margin: "auto",
                width: availableTenantTypes.length > 0 ? "96px" : "130px",
                height: availableTenantTypes.length > 0 ? "96px" : "130px",
              }}
            />
          </Box>

          {isResetPasswordRoute
            ? renderResetPasswordForm()
            : showForgotPassword
              ? renderForgotPasswordForm()
              : availableTenantTypes.length > 0
                ? renderTenantSelection()
                : (
            <>
          {errorKeys.length > 0 && (
            <Box marginBottom={2}>
              {errorKeys.map((key, i) => (
                <Typography key={i} color="error">
                  {t(key)}
                </Typography>
              ))}
            </Box>
          )}

          <TextField
            sx={{
              marginBottom: '20px',
              '& .MuiOutlinedInput-root': {
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.04)'
                    : 'rgba(255,255,255,0.7)',
              },
            }}
            label={t('email')}
            fullWidth
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              resetTenantSelection();
            }}
            margin="normal"
            error={username !== '' && !isUsernameValid}
          />

          <TextField
            sx={{
              marginBottom: '20px',
              '& .MuiOutlinedInput-root': {
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.04)'
                    : 'rgba(255,255,255,0.7)',
              },
            }}
            label={t('password')}
            type={showPassword ? "text" : "password"}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <Button
                    color='primary'
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                  >
                    {showPassword ? <VisibilityOffIcon fontSize='small' /> : <VisibilityIcon fontSize='small' />}
                  </Button>
                </InputAdornment>
              )
            }}
            fullWidth
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              resetTenantSelection();
            }}
            margin="normal"
          />

          <Box justifyContent={"flex-end"} display={"flex"} alignItems={"center"} sx={{ mb: 1 }}>
            <Link
              component="button"
              type="button"
              onClick={() => {
                setShowForgotPassword(true);
                setResetEmail(username || '');
                setResetError('');
              }}
            >
              Password dimenticata?
            </Link>
          </Box>

          <Button
            variant='contained'
            color='primary'
            fullWidth
            onClick={() => handleLogin()}
            disabled={!isUsernameValid || loading}
            sx={{ height: '48px', marginTop: '10px' }}
          >
            {loading ? t('loading') : t('login')}
          </Button>

          {/* <Typography paddingTop={"2vh"}>Non hai un account? <Link href="#">Registrati</Link></Typography> */}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
